<template>
    <DatePicker
        ref="picker"
        v-bind="$attrs"
        :range="range"
        :confirm="confirm"
        :shortcuts="innerShortcuts"
        :range-separator="rangeSeparator"
        :lang="locale"
        v-on="$listeners"
    >
        <template v-if="$slots.header" #header>
            <slot name="header"></slot>
        </template>

        <template v-if="confirm === true" #footer>
            <div class="d-flex gap-3 justify-content-end">
                <ButtonIcon variant="secondary" @click="handleCancel()">{{ $t('cancel') }}</ButtonIcon>
                <ButtonIcon variant="primary" @click="$refs.picker && $refs.picker.handleConfirmDate()">{{ $t('apply') }}</ButtonIcon>
            </div>
        </template>
    </DatePicker>
</template>



<script>
    import DatePicker from 'vue2-datepicker';
    import 'vue2-datepicker/index.css';
    import en from 'vue2-datepicker/locale/en';
    import pl from 'vue2-datepicker/locale/pl';
    import { TIME_RANGES, AVAILABLE_OPTIONS, dateWithoutUTC } from '@/utils/timeRanges';

    DatePicker.methods.renderSidebar = function renderSidebar() {
        const h = this.$createElement;
        const prefixClass = this.prefixClass;
        return h(
            'div',
            {
                class: ''.concat(prefixClass, '-datepicker-sidebar'),
            },
            [
                this.renderSlot('sidebar', null, {
                    value: this.currentValue,
                    emit: this.handleSelectDate,
                }),
                this.shortcuts.map((v, i) => {
                    let isActive = false;
                    const itemDate = v.onClick(this);
                    if (Array.isArray(itemDate)) {
                        isActive = this.formatDate(itemDate[0]) === this.formatDate(this.currentValue[0]) && this.formatDate(itemDate[1]) === this.formatDate(this.currentValue[1]);
                    } else {
                        isActive = this.formatDate(itemDate) === this.formatDate(this.currentValue);
                    }
                    return h(
                        'button',
                        {
                            key: i,
                            attrs: {
                                'data-index': i,
                                type: 'button',
                            },
                            class: ''
                                .concat(prefixClass, '-btn ')
                                .concat(prefixClass, '-btn-text ')
                                .concat(prefixClass, '-btn-shortcut')
                                .concat(isActive ? ' active ' : ''),
                            on: {
                                click: this.handleSelectShortcut,
                            },
                        },
                        [v.text]
                    );
                }),
            ]
        );
    };

    DatePicker.methods.handleSelectShortcut = function handleSelectShortcut(evt) {
        const index = evt.currentTarget.getAttribute('data-index');
        const item = this.shortcuts[parseInt(index, 10)];

        const isObject = obj => Object.prototype.toString.call(obj) === '[object Object]';

        if (isObject(item) && typeof item.onClick === 'function') {
            const date = item.onClick(this);
            this.$emit('preset-select', date, item.name, item.label);

            if (date) {
                if (this.confirm) {
                    this.currentValue = date;
                } else {
                    this.emitValue(date);
                }
            }
        }
    };

    export default {
        name: 'FormPickerDate',
        components: {
            DatePicker,
        },
        props: {
            range: {
                type: Boolean,
                default: false,
            },
            confirm: {
                type: Boolean,
                default: false,
            },
            shortcuts: {
                type: Boolean,
                default: false,
            },
            rangeSeparator: {
                type: String,
                default: ' - ',
            },
            options: {
                type: Array,
                default: () => [...AVAILABLE_OPTIONS],
            },
        },
        computed: {
            locale() {
                const locales = { en, pl };
                return locales[this.$i18n.locale] || en;
            },
            innerShortcuts() {
                const keys = AVAILABLE_OPTIONS.filter(name => name !== 'custom').filter(name => this.options.includes(name));
                return keys.map(name => {
                    const { startDate, endDate, label } = TIME_RANGES[name]();
                    const start = dateWithoutUTC(startDate);
                    const end = dateWithoutUTC(endDate);
                    const dateValue = this.range ? [start, end] : start;
                    return {
                        name,
                        text: label,
                        onClick: () => dateValue,
                    };
                });
            },
        },
        methods: {
            handleCancel() {
                if (this.range) {
                    this.$refs.picker.currentValue = [...this.$refs.picker.value];
                } else {
                    this.$refs.picker.currentValue = this.$refs.picker.value;
                }
                this.$refs.picker.$emit('cancel');
            },
        },
        mounted() {
            this.$nextTick(() => {
                this.$watch(
                    () => this.$refs.picker.currentValue,
                    newVal => this.$emit('select', newVal),
                    { deep: true, immediate: true }
                );
            });
        },
    };
</script>



<style lang="scss" scoped>
    @import 'vue2-datepicker/scss/var.scss';

    ::v-deep {
        .mx-datepicker-main {
            width: auto;
            display: flex;

            .mx-datepicker-sidebar {
                width: auto;
                float: unset;
            }

            .mx-btn-shortcut {
                &.active {
                    color: $time-active-color;
                }
            }

            .mx-datepicker-content {
                width: auto;
                float: unset;
                margin: 0;
            }

            .mx-btn.mx-datepicker-btn-confirm {
                display: none !important;
            }

            @media (max-width: 750px) {
                .mx-calendar + .mx-calendar {
                    border-left: 0;
                }
            }

            @media (max-width: 460px) {
                flex-direction: column;

                .mx-datepicker-sidebar {
                    border-bottom: 1px solid $border-color;
                }

                .mx-datepicker-sidebar + .mx-datepicker-content {
                    border-left: 0;
                }
            }
        }
    }
</style>
